/**
 * What the system Back gesture MEANS — pure, so it is provable (M10 / S10-03B).
 *
 * The device half of this lives in `src/platform/system-back.ts`. Everything
 * here is arithmetic over two numbers and a boolean: no React, no DOM, no
 * router, no plugin. That matters because the failure modes Jerry named —
 * "history loops", "fake entries forever", "exit while internal history
 * remains", "trap the user so the app can never leave" — are all statements
 * about a decision, and a decision this small should be readable in one screen
 * and testable without a phone.
 *
 * THE SMALLEST PREDICTABLE POLICY, and why it is this one:
 *
 * React Router's browser history stamps its own position into
 * `window.history.state.idx` — 0 for the entry the document opened on, one
 * higher for every entry the app has PUSHED since. That number, not a counter
 * of our own, is the honest answer to "does Crewlink have somewhere of its own
 * to go back to", and it needs nothing pushed, tracked or invented to obtain.
 *
 *   * `idx > 0` → the app pushed this entry, so Back consumes exactly one.
 *   * `idx === 0` → this is where the app started. Back leaves the app, which
 *     is what a normal Android app does at its root.
 *
 * WHY NOT `canGoBack` FIRST. The shell reports whether the WEB VIEW can go
 * back, which is a different and larger question: it includes entries from
 * before Crewlink was loaded. Trusting it at the root would walk backwards out
 * of the app's own history into whatever preceded it — Jerry's "stale routes"
 * and "return to sign-in unexpectedly". It is kept as the FALLBACK, for the
 * case where the router's index cannot be read at all, because in that case an
 * unanswered Back is worse than an imperfect one.
 *
 * WHY REPLACE-NAVIGATION IS THE REASON THIS IS SAFE: signing in and the
 * authentication redirects use `<Navigate replace>`, which does not advance
 * `idx`. So a signed-in Brandon standing on Projects reads `idx === 0` and Back
 * leaves the app — it can never step backwards onto the sign-in screen he just
 * came through.
 */

/** The only two things Back is ever allowed to do. */
export type SystemBackDecision = 'history-back' | 'exit-app';

export interface SystemBackInput {
  /**
   * React Router's position in the history stack, or `null` when it cannot be
   * read (a runtime without `history.state`, or a state object written by
   * something other than the router).
   */
  readonly routerIndex: number | null;
  /** What the shell reported. Consulted only when `routerIndex` is `null`. */
  readonly canGoBack: boolean;
}

/**
 * Decide, with no side effects.
 *
 * Deliberately total: every combination has an answer, and none of them is
 * "do nothing". A Back that does nothing is indistinguishable from a broken
 * phone, and it is precisely what the plugin does on its own at the root.
 */
export function decideSystemBack({ routerIndex, canGoBack }: SystemBackInput): SystemBackDecision {
  if (routerIndex !== null) {
    return routerIndex > 0 ? 'history-back' : 'exit-app';
  }
  return canGoBack ? 'history-back' : 'exit-app';
}

/** The slice of `window.history` this reads. Structural, so tests need no DOM. */
export interface HistoryLike {
  readonly state: unknown;
}

/**
 * Read React Router's index out of the history state.
 *
 * STRICT ON PURPOSE. Anything that is not a finite, non-negative integer is
 * `null` — not 0. Answering 0 for a malformed state would mean "you are at the
 * root", and the action at the root is to LEAVE THE APP: a value we do not
 * understand would close Crewlink over an in-progress report. `null` instead
 * falls through to the shell's own answer, which is the conservative direction.
 */
export function readRouterHistoryIndex(history: HistoryLike | undefined | null): number | null {
  if (history === undefined || history === null) return null;
  const state = history.state;
  if (typeof state !== 'object' || state === null) return null;
  const index = (state as { idx?: unknown }).idx;
  if (typeof index !== 'number' || !Number.isInteger(index) || index < 0) return null;
  return index;
}
